import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AddRounded, DeleteOutlineRounded } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTranslation } from 'react-i18next';
import {
  gradingScaleProblem,
  LETTER_4_SCALE,
  NUMERIC_SCALE_PRESETS,
  type GradingScale,
  type LetterGradeRow,
  type NumericScalePreset,
} from '@dosfilos/domain';
import { tenantConfigApi } from '../../../lib/api/tenant-config.api';
import { errorText } from '../../../lib/error-text';
import { useTenantConfig } from '../../../hooks/use-tenant-config';

// ─── La escala de notas del seminario: UNA sola ─────────────────────────────
// Numérica (1-7, 0-10, 0-20, 0-100, con su nota de aprobación) o de letras
// (A–F de 4 puntos, o una tabla propia). La guarda `updateTenantConfig`, que
// exige administración del seminario o plataforma, la valida y la audita. Se
// guarda con su propio botón: cambia cómo se califica TODO el seminario.

type Choice = NumericScalePreset | 'letter-4' | 'letter-custom';

const choiceOf = (scale: GradingScale): Choice =>
  scale.kind === 'numeric'
    ? scale.preset
    : scale.preset === 'letter-4'
      ? 'letter-4'
      : 'letter-custom';

const sameScale = (a: GradingScale, b: GradingScale) => JSON.stringify(a) === JSON.stringify(b);

export function GradingScaleEditor({ tenantId }: { tenantId: string }): JSX.Element {
  const { t } = useTranslation('sis');
  const queryClient = useQueryClient();
  const config = useTenantConfig();
  const saved = config.gradingScale;
  const [draft, setDraft] = useState<GradingScale>(saved);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [ask, setAsk] = useState(false);

  // Cuando llega (o cambia) la escala guardada, el borrador parte de ella.
  useEffect(() => setDraft(saved), [saved]);

  // ¿Ya hay notas finales? Entonces la escala no se reinterpreta: el servidor
  // lo rechaza (grading.scale_locked) y la pantalla lo dice ANTES de intentar.
  const lockedQ = useQuery({
    queryKey: ['grading-scale-locked', tenantId],
    queryFn: async () =>
      !(
        await getDocs(
          query(
            collection(db, `tenants/${tenantId}/enrollments`),
            where('status', 'in', ['approved', 'failed']),
            limit(1),
          ),
        )
      ).empty,
    staleTime: 60_000,
  });
  const locked = lockedQ.data === true;

  const problem = useMemo(() => gradingScaleProblem(draft), [draft]);
  const changed = !sameScale(draft, saved);
  // Reexpresar las notas de aprobación de las plantillas solo tiene sentido
  // entre escalas NUMÉRICAS (en letras, aprueba lo que diga la tabla).
  const canConvert =
    saved.kind === 'numeric' &&
    draft.kind === 'numeric' &&
    (saved.preset !== draft.preset || saved.exigencia !== draft.exigencia);

  const choose = (choice: Choice) => {
    if (choice === 'letter-4') return setDraft(LETTER_4_SCALE);
    if (choice === 'letter-custom') {
      return setDraft({
        kind: 'letter',
        preset: 'custom',
        signsIgnored: true,
        rows: (draft.kind === 'letter' ? draft.rows : LETTER_4_SCALE.rows).map((r) => ({ ...r })),
      });
    }
    setDraft(NUMERIC_SCALE_PRESETS[choice]);
  };

  async function save(convert: boolean) {
    setBusy(true);
    setMsg(null);
    try {
      await tenantConfigApi.updateTenantConfig({
        tenantId,
        gradingScale: draft,
        convertPassingGrades: convert,
      });
      await queryClient.invalidateQueries({ queryKey: ['tenant-config-view', tenantId] });
      setMsg({ ok: true, text: t('settings.grading.saved') });
    } catch (e) {
      setMsg({ ok: false, text: errorText(e, t('settings.grading.saveError')) });
    } finally {
      setBusy(false);
      setAsk(false);
    }
  }

  const setRow = (i: number, patch: Partial<LetterGradeRow>) =>
    setDraft((d) =>
      d.kind === 'letter'
        ? { ...d, rows: d.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) }
        : d,
    );

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
        {t('settings.grading.title')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {t('settings.grading.help')}
      </Typography>

      <Stack spacing={2}>
        {locked && <Alert severity="info">{t('settings.grading.locked')}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            select
            size="small"
            label={t('settings.grading.scaleLabel')}
            value={choiceOf(draft)}
            onChange={(e) => choose(e.target.value as Choice)}
            disabled={locked}
            sx={{ minWidth: 260 }}
          >
            <MenuItem value="1-7">{t('settings.grading.scale17')}</MenuItem>
            <MenuItem value="0-10">{t('settings.grading.scale010')}</MenuItem>
            <MenuItem value="0-20">{t('settings.grading.scale020')}</MenuItem>
            <MenuItem value="0-100">{t('settings.grading.scale0100')}</MenuItem>
            <MenuItem value="letter-4">{t('settings.grading.letter4')}</MenuItem>
            <MenuItem value="letter-custom">{t('settings.grading.letterCustom')}</MenuItem>
          </TextField>

          {draft.kind === 'numeric' && (
            <TextField
              size="small"
              type="number"
              label={t('settings.grading.passingLabel')}
              value={draft.passing}
              onChange={(e) => setDraft({ ...draft, passing: Number(e.target.value) })}
              helperText={t('settings.grading.passingHelp', { min: draft.min, max: draft.max })}
              slotProps={{ htmlInput: { step: draft.decimals > 0 ? 0.1 : 1 } }}
              sx={{ width: 220 }}
            />
          )}
          {draft.kind === 'numeric' && draft.preset === '1-7' && (
            <TextField
              size="small"
              type="number"
              label={t('settings.grading.exigenciaLabel')}
              value={draft.exigencia !== undefined ? Math.round(draft.exigencia * 100) : ''}
              onChange={(e) => {
                const v = e.target.value;
                const { exigencia: _old, ...rest } = draft;
                setDraft(v === '' ? rest : { ...rest, exigencia: Number(v) / 100 });
              }}
              helperText={t('settings.grading.exigenciaHelp')}
              sx={{ width: 220 }}
            />
          )}
        </Stack>

        {draft.kind === 'letter' && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {draft.preset === 'letter-4'
                ? t('settings.grading.letter4Help')
                : t('settings.grading.lettersHelp')}
            </Typography>
            <Table size="small" sx={{ maxWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('settings.grading.colLetter')}</TableCell>
                  <TableCell>{t('settings.grading.colPoints')}</TableCell>
                  <TableCell>{t('settings.grading.colMinPercent')}</TableCell>
                  <TableCell>{t('settings.grading.colPasses')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {draft.rows.map((row, i) => {
                  const ro = draft.preset === 'letter-4' || locked;
                  return (
                    <TableRow key={i}>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.label}
                          disabled={ro}
                          onChange={(e) => setRow(i, { label: e.target.value.toUpperCase() })}
                          slotProps={{
                            htmlInput: {
                              maxLength: 4,
                              'aria-label': t('settings.grading.colLetter'),
                            },
                          }}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={row.points}
                          disabled={ro}
                          onChange={(e) => setRow(i, { points: Number(e.target.value) })}
                          slotProps={{
                            htmlInput: { 'aria-label': t('settings.grading.colPoints') },
                          }}
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={row.minPercent}
                          disabled={ro}
                          onChange={(e) => setRow(i, { minPercent: Number(e.target.value) })}
                          slotProps={{
                            htmlInput: { 'aria-label': t('settings.grading.colMinPercent') },
                          }}
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={row.passes}
                          disabled={ro}
                          onChange={(e) => setRow(i, { passes: e.target.checked })}
                          inputProps={{ 'aria-label': t('settings.grading.colPasses') }}
                        />
                      </TableCell>
                      <TableCell>
                        {!ro && (
                          <IconButton
                            size="small"
                            aria-label={t('settings.grading.removeRow')}
                            onClick={() =>
                              setDraft({ ...draft, rows: draft.rows.filter((_, j) => j !== i) })
                            }
                          >
                            <DeleteOutlineRounded fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {draft.preset === 'custom' && !locked && (
              <Button
                size="small"
                startIcon={<AddRounded />}
                sx={{ mt: 1 }}
                onClick={() =>
                  setDraft({
                    ...draft,
                    rows: [...draft.rows, { label: '', points: 0, minPercent: 0, passes: false }],
                  })
                }
              >
                {t('settings.grading.addRow')}
              </Button>
            )}
          </Box>
        )}

        {problem && changed && (
          <Alert severity="warning">{t(`errors.${problem}`, { ns: 'common' })}</Alert>
        )}

        <Box>
          <Button
            variant="outlined"
            disabled={busy || !changed || problem !== null}
            onClick={() => (canConvert ? setAsk(true) : void save(false))}
          >
            {busy ? t('settings.grading.saving') : t('settings.grading.apply')}
          </Button>
        </Box>
        {msg && <Alert severity={msg.ok ? 'success' : 'error'}>{msg.text}</Alert>}
      </Stack>

      <Dialog open={ask} onClose={() => setAsk(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('settings.grading.confirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {t('settings.grading.confirmBodyScales')}
          </Typography>
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {t('settings.grading.confirmPassing')} {t('settings.grading.confirmFix')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAsk(false)}>{t('settings.grading.cancel')}</Button>
          <Button onClick={() => void save(false)} disabled={busy}>
            {t('settings.grading.withoutConvert')}
          </Button>
          <Button variant="contained" onClick={() => void save(true)} disabled={busy}>
            {t('settings.grading.convert')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
